import { useMemo, useState } from 'react';
import { SearchValue } from '../components/SearchableHeader';
import ISearcher from '../ISearcher';

interface searchMapValue<T> {
  value: T;
  compare: number;
}

/**
 * Searches items such that the best results are returned.
 * @param items The array of items
 * @param searcher The search that yields the comparison values.
 * @param search The search parameters. If undefined, all items are returned.
 * @returns The items that pass the search and sorted so better matches are first.
 */
export const searchItems = <T, TSearchProps>(
  items: T[],
  searcher: ISearcher<T, TSearchProps>,
  search?: SearchValue<TSearchProps>,
) => {
  if (!search || !search.value) return items;

  const expected = typeof search.value === 'string'
    ? search.value.toLowerCase().replace(/\s+/g, '')
    : search.value;

  /**
   * Reduced items such that only the best searches by type are included.
   */
  const reduced = items.reduce((result: searchMapValue<T>[], current) => {
    // Find the best search type for the current value.
    const best = search.types.reduce((previousSearch: searchMapValue<T> | undefined, sType) => {
      // Perform the search and get the comparison value, lower is better.
      const compare = searcher.search(current, expected, sType);
      if (compare !== undefined) {
        const sMap: searchMapValue<T> = {
          value: current,
          compare,
        };
        // If the previous search type yielded no result OR this result is better than the previous.
        if (previousSearch === undefined || sMap.compare <= previousSearch.compare) {
          // Yield this result.
          return sMap;
        }
      }
      // By default yield the previous result, which can be undefined.
      return previousSearch;
    }, undefined);
    // If a search result was gotten, push it to results.
    if (best !== undefined) result.push(best);
    return result;
  }, []);

  // Sort the results by their comparison distance, such that better results are first.
  reduced.sort((a, b) => a.compare - b.compare);

  // Return just the values.
  return reduced.map((r) => r.value);
};

/**
 * The return type from the useSearch hook.
 * First element is the resulting items.
 * Second element is current search parameters.
 * Third element is a function to change the search parameters.
 */
type ReturnType<T, TSearchProps> = [
  T[],
  SearchValue<TSearchProps> | undefined,
  (search?: SearchValue<TSearchProps> | undefined) => void
];
/**
 * A hook that performs searches on a number of items.
 * @param initialItems The initial input of items.
 * @param searcher The searcher that performs the comparisons.
 * @param initialSearch The initial search parameters.
 * @returns An array of items, the current search parameters and a function to update it.
 */
export const useSearch = <T, TSearchProps>(
  initialItems: T[],
  searcher: ISearcher<T, TSearchProps>,
  initialSearch?: SearchValue<TSearchProps>,
): ReturnType<T, TSearchProps> => {
  const [search, setSearch] = useState<SearchValue<TSearchProps> | undefined>(
    initialSearch,
  );

  const resultItems = useMemo(
    () => searchItems(initialItems, searcher, search),
    [initialItems, searcher, search],
  );

  return [resultItems, search, setSearch];
};

export default useSearch;
